import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { EMPTY } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ApiError, ConfigService } from '../../../../core';
import { ConfigEmpresa } from '../../../../home/configuracoes/app/empresa/config-empresa';
import { isIdentificacaoRepresentanteNome } from '../../../../home/configuracoes/app/empresa/config-empresa-identificacao-representante';
import {
  representanteAtivo,
  representanteInativo,
  RepresentanteFilter,
  RepresentanteSearchHttpService
} from '../../../../home/pessoa/representante';
import { RepresentanteNomePipe } from '../../../../home/pessoa/representante/pipes/representante-nome.pipe';
import { RepresentanteSearchOrderBy } from '../../../../home/pessoa/representante/representante-search-orderby';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { IOption } from '../multi-select/models/option.interface';
import { ToastService } from '../toast/services/toast.service';

@Component({
  selector: 'sgt-novo-representante-multi-select',
  templateUrl: './novo-representante-multi-select.component.html',
  styleUrls: ['./novo-representante-multi-select.component.scss'],
  providers: [
    RepresentanteNomePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoRepresentanteMultiSelectComponent),
      multi: true
    }
  ]
})
export class NovoRepresentanteMultiSelectComponent
  extends CustomControlValueAccessor<number[]>
  implements OnInit, OnChanges {
  @Input() idsRepresentantes: number[];
  @Input() activeOnly = false;
  @Input() variante: 'filtro' | undefined;
  @Input() icon = 'fa-user-tie';
  @Output() search = new EventEmitter<number[]>();

  options: IOption[] = [];
  idsInativos = new Set<number>();
  configEmpresa: ConfigEmpresa;

  @Input() settings: IMultiSelectSettings;
  defaultSettings: IMultiSelectSettings = {
    placeholder: 'Representantes',
    singular: 'Representante',
    plural: 'Representantes',
    searchEmptyResultMessage: 'Nenhum representante encontrado',
    isLoading: false,
    isDisabled: false
  };

  constructor(
    private configService: ConfigService,
    private toastService: ToastService,
    private representanteNomePipe: RepresentanteNomePipe,
    private representanteSearchHttpService: RepresentanteSearchHttpService
  ) {
    super();
  }

  ngOnInit() {
    this.settings = {
      ...this.defaultSettings,
      ...this.settings
    };

    this.configEmpresa = this.configService.getEmpresa();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['idsRepresentantes'] &&
      !changes['idsRepresentantes'].isFirstChange()
    ) {
      this.idsRepresentantes = this.idsRepresentantes ?? [];
    }
  }

  hasOpenedFirstTime(isFirstOpened: boolean) {
    if (isFirstOpened && this.options.length === 0) {
      this.fetchRepresentantes().subscribe();
    }
  }

  private fetchRepresentantes() {
    if (this.settings.isLoading || this.options.length > 0) {
      return EMPTY;
    }

    this.settings = { ...this.settings, isLoading: true };

    const filter = new RepresentanteFilter();
    const orderBy = this.setOrderBy();

    if (this.activeOnly) {
      filter.situacoes = [representanteAtivo.id];
    }

    return this.representanteSearchHttpService
      .search(filter, orderBy, 'asc', -1, -1)
      .pipe(
        take(1),
        tap({
          next: (response: any) => {
            this.options = this.convertDTOtoOptions(response.representantes);
            this.settings = { ...this.settings, isLoading: false };
          },
          error: (error: ApiError) => {
            this.settings = { ...this.settings, isLoading: false };
            this.toastService.error(error.message, {
              title: error.cause || 'Erro ao carregar representantes',
              duration: 5000
            });
          }
        })
      );
  }

  private setOrderBy(): RepresentanteSearchOrderBy {
    if (
      isIdentificacaoRepresentanteNome(
        this.configEmpresa.identificacaoRepresentante
      )
    ) {
      return RepresentanteSearchOrderBy.Nome;
    }
    return RepresentanteSearchOrderBy.NomeFantasia;
  }

  private convertDTOtoOptions(data: any[]): IOption[] {
    this.idsInativos = new Set(
      data
        .filter((item) => item.situacao === representanteInativo.id)
        .map((item) => item.id)
    );

    const ativosPrimeiro = [...data].sort(
      (a, b) =>
        Number(this.idsInativos.has(a.id)) - Number(this.idsInativos.has(b.id))
    );

    return ativosPrimeiro.map((item) => ({
      id: item.id,
      label: this.representanteNomePipe.transform(item.nome, item.nomeFantasia),
      isChecked: (this.idsRepresentantes ?? []).includes(item.id)
    }));
  }

  onSearch() {
    this.search.emit(this.idsRepresentantes);
  }

  onSelectionChange(): void {
    this.emitChange(this.idsRepresentantes);
  }

  protected updateValue(value: number[] | null): void {
    this.idsRepresentantes = value || [];
    if ((value ?? []).length > 0 && this.options.length === 0) {
      this.fetchRepresentantes().subscribe();
    }
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = {
      ...this.settings,
      isDisabled
    };
  }
}
